import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  TestTube, CheckCircle, XCircle, AlertTriangle, User, FileText,
  Eye, Upload, Trash2, ArrowLeft, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Drug Test Panels
const DRUG_PANELS = [
  { id: 5, name: '5-Panel', substances: ['Amphetamines', 'Cocaine', 'Marijuana (THC)', 'Opiates', 'PCP'] },
  { id: 6, name: '6-Panel', substances: ['Amphetamines', 'Barbiturates', 'Cocaine', 'Marijuana (THC)', 'Opiates', 'PCP'] },
  { id: 7, name: '7-Panel', substances: ['Amphetamines', 'Barbiturates', 'Benzodiazepines', 'Cocaine', 'Marijuana (THC)', 'Opiates', 'PCP'] },
  { id: 8, name: '8-Panel', substances: ['Amphetamines', 'Barbiturates', 'Benzodiazepines', 'Cocaine', 'Marijuana (THC)', 'Methadone', 'Opiates', 'PCP'] },
  { id: 9, name: '9-Panel', substances: ['Amphetamines', 'Barbiturates', 'Benzodiazepines', 'Cocaine', 'Marijuana (THC)', 'Methadone', 'Methaqualone', 'Opiates', 'PCP'] },
  { id: 10, name: '10-Panel', substances: ['Amphetamines', 'Barbiturates', 'Benzodiazepines', 'Cocaine', 'Marijuana (THC)', 'Methadone', 'Methaqualone', 'Opiates', 'PCP', 'Propoxyphene'] },
  { id: 11, name: '11-Panel', substances: ['Amphetamines', 'Barbiturates', 'Benzodiazepines', 'Cocaine', 'EDDP', 'Marijuana (THC)', 'Methadone', 'Methaqualone', 'Opiates', 'PCP', 'Propoxyphene'] },
  { id: 12, name: '12-Panel', substances: ['Amphetamines', 'Barbiturates', 'Benzodiazepines', 'Buprenorphine', 'Cocaine', 'EDDP', 'Marijuana (THC)', 'Methadone', 'Methaqualone', 'Opiates', 'PCP', 'Propoxyphene'] }
];

export default function DrugTestForm({ 
  caseId, 
  checkId, 
  checkData, 
  employeeData,
  onBack,
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [showRecordResponse, setShowRecordResponse] = useState(false);
  const [verificationData, setVerificationData] = useState(checkData?.drug_test || {});
  
  // Annexure state
  const [annexures, setAnnexures] = useState([]);
  const [showUploadAnnexure, setShowUploadAnnexure] = useState(false);
  const [uploadingAnnexure, setUploadingAnnexure] = useState(false);
  const [annexureForm, setAnnexureForm] = useState({
    file_name: '',
    file_url: '',
    file_type: 'document',
    description: '',
    annexure_type: 'drug_test_report'
  });
  
  // Response form state
  const [responseForm, setResponseForm] = useState({
    agent_name: '',
    test_date: '',
    panel_selected: '5',
    lab_name: '',
    sample_id: '',
    collection_date: '',
    report_date: '',
    results: [],
    overall_result: 'negative',
    mro_verified: 'yes',
    agent_remarks: '',
    overall_status: 'verified'
  });

  useEffect(() => {
    fetchAnnexures();
  }, [caseId, checkId]);

  useEffect(() => {
    // Update results when panel changes
    const panel = DRUG_PANELS.find(p => p.id === parseInt(responseForm.panel_selected));
    if (panel) {
      setResponseForm(prev => ({
        ...prev,
        results: panel.substances.map(s => ({
          substance: s,
          result: 'negative',
          cutoff_level: '',
          remarks: ''
        }))
      }));
    }
  }, [responseForm.panel_selected]);

  const fetchAnnexures = async () => {
    try {
      const res = await axios.get(`/cases/${caseId}/checks/${checkId}/annexures`);
      setAnnexures(res.data || []);
    } catch (e) {
      console.error('Failed to fetch annexures');
    }
  };

  const handleUploadAnnexure = async () => {
    if (!annexureForm.file_url) {
      toast.error('Please provide file URL');
      return;
    }
    
    setUploadingAnnexure(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures`, {
        case_id: caseId,
        check_id: checkId,
        ...annexureForm
      });
      setAnnexures(prev => [...prev, res.data]);
      setShowUploadAnnexure(false);
      setAnnexureForm({
        file_name: '',
        file_url: '',
        file_type: 'document',
        description: '',
        annexure_type: 'drug_test_report'
      });
      toast.success('Report uploaded');
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploadingAnnexure(false);
    }
  };

  const handleDeleteAnnexure = async (annexureId) => {
    if (!confirm('Delete this annexure?')) return;
    try {
      await axios.delete(`/annexures/${annexureId}`);
      setAnnexures(prev => prev.filter(a => a.id !== annexureId));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleRecordResponse = async () => {
    if (!responseForm.agent_name || !responseForm.test_date) {
      toast.error('Please fill agent name and test date');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/cases/${caseId}/checks/${checkId}/record-drug-test`, {
        ...responseForm,
        check_type: 'drug_test'
      });
      toast.success(`Drug test recorded - Result: ${responseForm.overall_result.toUpperCase()}`);
      setVerificationData(prev => ({ ...prev, ...res.data }));
      setShowRecordResponse(false);
      if (onComplete) onComplete(res.data);
    } catch (error) {
      toast.error('Failed to record');
    } finally {
      setLoading(false);
    }
  };

  const updateResult = (index, field, value) => {
    setResponseForm(prev => {
      const newResults = [...prev.results];
      newResults[index] = { ...newResults[index], [field]: value };
      return { ...prev, results: newResults };
    });
  };

  const getResultBadge = (result) => {
    switch(result) {
      case 'negative': return <Badge className="bg-emerald-100 text-emerald-700">NEGATIVE</Badge>;
      case 'positive': return <Badge className="bg-rose-100 text-rose-700">POSITIVE</Badge>;
      case 'inconclusive': return <Badge className="bg-amber-100 text-amber-700">INCONCLUSIVE</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  const selectedPanel = DRUG_PANELS.find(p => p.id === parseInt(responseForm.panel_selected));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <TestTube className="w-6 h-6 text-amber-600" />
              Drug Test Verification
            </h2>
            <p className="text-slate-600">Case ID: {caseId} | Check ID: {checkId}</p>
          </div>
        </div>
        {getResultBadge(verificationData?.overall_result)}
      </div>

      {/* Candidate Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-amber-600" />
            Candidate Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-500">Name</Label>
              <p className="font-medium">{employeeData?.name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Date of Birth</Label>
              <p className="font-medium">{employeeData?.dob || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Contact</Label>
              <p className="font-medium">{employeeData?.phone || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Email</Label>
              <p className="font-medium">{employeeData?.email || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Panel Selection */}
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5 text-amber-600" />
            Select Drug Test Panel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DRUG_PANELS.map(panel => (
              <div 
                key={panel.id}
                onClick={() => setResponseForm(prev => ({ ...prev, panel_selected: panel.id.toString() }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  responseForm.panel_selected === panel.id.toString()
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 hover:border-amber-300'
                }`}
              >
                <p className="font-bold text-lg">{panel.name}</p>
                <p className="text-sm text-slate-500">{panel.substances.length} substances</p>
              </div>
            ))}
          </div>
          
          {selectedPanel && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <Label className="font-medium">Substances tested in {selectedPanel.name}:</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedPanel.substances.map((sub, idx) => (
                  <Badge key={idx} variant="outline">{sub}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Report */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Drug Test Report
          </CardTitle>
          <Button onClick={() => setShowUploadAnnexure(true)} size="sm" className="bg-amber-600 hover:bg-amber-700">
            <Upload className="w-4 h-4 mr-2" />
            Upload Report
          </Button>
        </CardHeader>
        <CardContent>
          {annexures.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No test report uploaded</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {annexures.map((annexure, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-slate-50 relative group">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-600 h-6 w-6 p-0"
                    onClick={() => handleDeleteAnnexure(annexure.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Badge variant="outline" className="text-xs mb-2">{annexure.annexure_type}</Badge>
                  <p className="text-sm font-medium truncate">{annexure.file_name || 'Report'}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2 text-xs h-7"
                    onClick={() => window.open(annexure.file_url, '_blank')}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button onClick={() => setShowRecordResponse(true)} className="bg-amber-600 hover:bg-amber-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Record Test Results
        </Button>
      </div>

      {/* Verification Result */}
      {verificationData?.overall_result && (
        <Card className={
          verificationData.overall_result === 'negative' ? 'border-emerald-200 bg-emerald-50' :
          verificationData.overall_result === 'positive' ? 'border-rose-200 bg-rose-50' :
          'border-amber-200 bg-amber-50'
        }>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Drug Test Result</span>
              {getResultBadge(verificationData.overall_result)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-500">Panel Used</Label>
                <p className="font-medium">{verificationData.panel_selected}-Panel</p>
              </div>
              <div>
                <Label className="text-slate-500">Lab Name</Label>
                <p className="font-medium">{verificationData.lab_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Test Date</Label>
                <p className="font-medium">{verificationData.test_date || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">MRO Verified</Label>
                <p className="font-medium capitalize">{verificationData.mro_verified || 'N/A'}</p>
              </div>
            </div>
            {verificationData.results && (
              <div className="mt-4">
                <Label className="font-medium">Substance Results:</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {verificationData.results.map((r, idx) => (
                    <div key={idx} className={`p-2 rounded ${r.result === 'negative' ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                      <p className="text-sm font-medium">{r.substance}</p>
                      <Badge className={r.result === 'negative' ? 'bg-emerald-600' : 'bg-rose-600'}>
                        {r.result.toUpperCase()}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponse} onOpenChange={setShowRecordResponse}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="w-5 h-5" />
              Record Drug Test Results ({selectedPanel?.name})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Test Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Agent Name *</Label>
                <Input 
                  value={responseForm.agent_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, agent_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Lab Name</Label>
                <Input 
                  value={responseForm.lab_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, lab_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Sample ID</Label>
                <Input 
                  value={responseForm.sample_id}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, sample_id: e.target.value }))}
                />
              </div>
              <div>
                <Label>Test Date *</Label>
                <Input 
                  type="date"
                  value={responseForm.test_date}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, test_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Collection Date</Label>
                <Input 
                  type="date"
                  value={responseForm.collection_date}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, collection_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Report Date</Label>
                <Input 
                  type="date"
                  value={responseForm.report_date}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, report_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Substance Results */}
            <div>
              <h3 className="font-medium text-lg mb-4">Substance Test Results</h3>
              <div className="space-y-3">
                {responseForm.results.map((result, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border flex items-center gap-4">
                    <div className="flex-1">
                      <Label className="font-medium">{result.substance}</Label>
                    </div>
                    <div className="w-[150px]">
                      <Select 
                        value={result.result}
                        onValueChange={(v) => updateResult(idx, 'result', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="negative">NEGATIVE</SelectItem>
                          <SelectItem value="positive">POSITIVE</SelectItem>
                          <SelectItem value="inconclusive">INCONCLUSIVE</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-[100px]">
                      <Input 
                        placeholder="Cutoff"
                        value={result.cutoff_level}
                        onChange={(e) => updateResult(idx, 'cutoff_level', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Overall Result */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Label className="font-medium">Overall Test Result</Label>
              <RadioGroup 
                value={responseForm.overall_result}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, overall_result: v }))}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="negative" id="result-neg" />
                  <Label htmlFor="result-neg" className="text-emerald-700 cursor-pointer">NEGATIVE</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="positive" id="result-pos" />
                  <Label htmlFor="result-pos" className="text-rose-700 cursor-pointer">POSITIVE</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="inconclusive" id="result-inc" />
                  <Label htmlFor="result-inc" className="text-amber-700 cursor-pointer">INCONCLUSIVE</Label>
                </div>
              </RadioGroup>
            </div>

            {/* MRO Verification */}
            <div className="flex items-center gap-4">
              <Label>MRO (Medical Review Officer) Verified?</Label>
              <RadioGroup 
                value={responseForm.mro_verified}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, mro_verified: v }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="mro-yes" />
                  <Label htmlFor="mro-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="mro-no" />
                  <Label htmlFor="mro-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Agent Remarks */}
            <div>
              <Label>Agent Remarks</Label>
              <Textarea 
                value={responseForm.agent_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, agent_remarks: e.target.value }))}
                placeholder="Additional remarks about the drug test..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponse(false)}>Cancel</Button>
            <Button onClick={handleRecordResponse} disabled={loading} className="bg-amber-600 hover:bg-amber-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Results'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Annexure Dialog */}
      <Dialog open={showUploadAnnexure} onOpenChange={setShowUploadAnnexure}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-600" />
              Upload Drug Test Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type</Label>
              <Select 
                value={annexureForm.annexure_type}
                onValueChange={(v) => setAnnexureForm(prev => ({ ...prev, annexure_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="drug_test_report">Drug Test Report</SelectItem>
                  <SelectItem value="lab_certificate">Lab Certificate</SelectItem>
                  <SelectItem value="mro_report">MRO Report</SelectItem>
                  <SelectItem value="chain_of_custody">Chain of Custody Form</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File Name</Label>
              <Input 
                value={annexureForm.file_name}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>File URL *</Label>
              <Input 
                value={annexureForm.file_url}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_url: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={annexureForm.description}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAnnexure(false)}>Cancel</Button>
            <Button 
              onClick={handleUploadAnnexure} 
              disabled={uploadingAnnexure || !annexureForm.file_url} 
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingAnnexure ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
